import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { OrganizationDto } from '@sib/api';
import { Tooltip } from '@sib/ui';
import { paths } from '@spendbrain/config/paths';

interface OrganizationLogoProps {
  organization?: OrganizationDto;
}

const MAX_LOGO_HEIGHT = 44;

export const OrganizationLogo = ({ organization }: OrganizationLogoProps) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isImageError, setIsImageError] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const { width = 0, height = 0 } = organization || {};
  const finalWidth = width || imageDimensions?.width || 0;
  const finalHeight = height || imageDimensions?.height || 0;
  const aspectRatio =
    finalWidth && finalHeight ? finalWidth / finalHeight : 1.66;

  const calculatedLogoHeight = MAX_LOGO_HEIGHT;
  const calculatedLogoWidth = MAX_LOGO_HEIGHT * aspectRatio;

  useEffect(() => {
    setIsImageError(false);
    setImageDimensions(null);
  }, [organization]);

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  };

  const isTextOverflowing = textRef.current
    ? textRef.current.offsetHeight < textRef.current.scrollHeight ||
      textRef.current.offsetWidth < textRef.current.scrollWidth
    : false;

  return (
    <Link to={paths.dashboard} className="hidden sm:block">
      {organization?.fileUrl && !isImageError ? (
        <Tooltip
          TooltipTriggerProps={{ asChild: true }}
          content={organization.name}
        >
          <img
            src={organization?.fileUrl}
            alt="Company Logo"
            className="size-full object-fill"
            onLoad={handleImageLoad}
            onError={() => setIsImageError(true)}
            style={{
              height: calculatedLogoHeight,
              width: calculatedLogoWidth,
              maxHeight: MAX_LOGO_HEIGHT,
            }}
          />
        </Tooltip>
      ) : (
        <Tooltip
          TooltipTriggerProps={{ asChild: true }}
          content={isTextOverflowing && organization?.name}
        >
          <p
            ref={textRef}
            className="line-clamp-2 w-[260px] overflow-hidden text-base leading-5.5 font-semibold break-words text-ellipsis"
          >
            {organization?.name}
          </p>
        </Tooltip>
      )}
    </Link>
  );
};
